import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FadeIn, StaggeredGrid } from "@/components/animated";
import { offeringsApi } from "@/features/offerings/api/offeringsApi";
import { OfferingCard } from "@/features/offerings/components/OfferingCard";
import { Loader2 } from "lucide-react";

type Tab = "programas" | "capacitaciones";

const TABS: Array<{ id: Tab; label: string; category: string; blurb: string }> =
  [
    {
      id: "programas",
      label: "Programas",
      category: "PROGRAMA",
      blurb:
        "Procesos formativos de mayor alcance, estructurados en módulos o componentes que permiten abordar una necesidad de manera integral.",
    },
    {
      id: "capacitaciones",
      label: "Capacitaciones",
      category: "CAPACITACION",
      blurb:
        "Experiencias formativas enfocadas en desarrollar conocimientos y competencias específicas, con contenidos prácticos y aplicables al contexto laboral.",
    },
  ];

export default function OfferingsPage() {
  // La categoría vive en la URL para que un enlace compartido abra la correcta
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("tipo");
  const active: Tab =
    requested === "capacitaciones" ? "capacitaciones" : "programas";
  const activeTab = TABS.find((t) => t.id === active)!;

  const { data: offerings, isLoading } = useQuery({
    queryKey: ["offerings", activeTab.category],
    queryFn: () => offeringsApi.listPublic(activeTab.category),
  });

  return (
    <div className="bg-white">
      <section className="bg-gradient-to-b from-secondary-50 to-white border-b">
        <div className="container py-16 md:py-20">
          <FadeIn>
            <div className="max-w-3xl">
              <h1 className="font-display text-4xl md:text-5xl font-bold text-secondary-900 mb-6">
                Formación especializada que responde a los retos de su
                organización
              </h1>
              <p className="text-lg text-secondary-600 leading-relaxed mb-4">
                Desarrollamos programas y capacitaciones para fortalecer
                conocimientos, competencias y capacidades en empresas e
                instituciones.
              </p>
              <p className="text-lg text-secondary-600 leading-relaxed">
                Cada propuesta puede adaptarse según los objetivos,
                características de la población y necesidades de su
                organización, ajustando contenidos, duración, modalidad y
                metodología.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      <div className="container py-12">
        <div
          className="inline-flex p-1 bg-secondary-100 rounded-xl mb-8"
          role="tablist"
          aria-label="Tipo de oferta formativa"
        >
          {TABS.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={active === tab.id}
              onClick={() => setSearchParams({ tipo: tab.id })}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                active === tab.id
                  ? "bg-white text-secondary-900 shadow-sm"
                  : "text-secondary-600 hover:text-secondary-900"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <p className="text-secondary-600 max-w-3xl mb-10 leading-relaxed">
          {activeTab.blurb}
        </p>

        {isLoading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : offerings && offerings.length > 0 ? (
          <StaggeredGrid className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {offerings.map((offering) => (
              <OfferingCard key={offering.id} offering={offering} />
            ))}
          </StaggeredGrid>
        ) : (
          <p className="text-secondary-500 py-16 text-center">
            Todavía no hay {activeTab.label.toLowerCase()} publicadas.
          </p>
        )}
      </div>
    </div>
  );
}
